<template>
	<div>
		<user-nav-menu bg-color="transparent" text-color="#ffffff" :logo-file-name="tenant.settings.brandImages.headerLogos.app.light"></user-nav-menu>
		<v-container class="mt-16">
			<v-row class="mb-8">
				<v-col class="text-center white--text">
					<h1>User Management</h1>
					<h4 class="font-weight-light mt-2">{{ getLabel('companyUserManagementPageDescription', 'Invite a team member to join your Practice') }}</h4>
				</v-col>
			</v-row>

			<v-row>
				<v-col offset-md="8" cols="12" md="2">
					<v-btn append outlined block dark @click="showPendingInvites"> Pending Invites </v-btn>
				</v-col>
				<v-col cols="12" md="2">
					<v-btn append dark color="primary" block :loading="loadingInvite" @click="newUserDialog = true"> Add User </v-btn>
				</v-col>
			</v-row>

			<v-data-table :headers="tableHeaders" :items="filteredUsers" item-key="id" :loading="loadingUsers" class="elevation-0 mt-2">
				<!-- eslint-disable-next-line -->
				<template v-slot:item.actions="{ item }">
					<v-icon disabled small class="mr-2"> mdi-pencil </v-icon>
				</template>
			</v-data-table>

			<v-row class="mt-8">
				<v-col class="text-center white--text">
					<v-btn
						class="pt-0 mt-0"
						link
						depressed
						color="primary"
						style="
							font-family: Montserrat;
							font-style: normal;
							font-weight: normal;
							font-size: 14px;
							line-height: 17px;
							text-align: right;
							text-decoration-line: underline;
						"
						to="/"
					>
						return to dashboard
					</v-btn>
				</v-col>
			</v-row>
		</v-container>

		<!-- New Invite Dialog -->
		<v-dialog v-model="newUserDialog" persistent max-width="600px">
			<v-card>
				<v-card-title>
					<span class="headline">Invite User</span>
				</v-card-title>
				<v-card-text>
					<v-container>
						<v-row>
							<v-col cols="12">
								<v-text-field v-model="invite.email" label="Email" :disabled="invite.inviteLink" required :rules="emailRules" />
							</v-col>
							<v-col cols="12">
								<v-autocomplete
									v-model="invite.roles"
									dense
									disabled
									hint="Coming soon"
									persistent-hint
									item-text="name"
									item-value="value"
									label="Roles"
									multiple
									chips
								/>
							</v-col>
						</v-row>
						<v-row v-if="invite.inviteLink">
							<v-col cols="12">
								<div class="text-center">User invitation sent by email.</div>
							</v-col>
						</v-row>
					</v-container>
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<v-btn color="blue darken-1" text :disabled="loadingInvite" @click="closeInviteDialog"> Close </v-btn>
					<v-btn v-if="!invite.inviteLink" color="blue darken-1" text :loading="loadingInvite" @click="sendInvite()"> Create Invite </v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Pending Invites Dialog -->
		<v-dialog v-model="pendingInviteDialog" persistent scrollable max-width="600px">
			<v-card>
				<v-card-title>
					<span class="headline">Pending Invites</span>
				</v-card-title>
				<v-card-text id="pending-invite-wrapper">
					<v-list two-line>
						<template v-if="pendingInvites.length > 0">
							<template v-for="(pendingInvite, i) in pendingInvites">
								<v-divider :key="i" />

								<v-list-item :key="pendingInvite.id">
									<v-list-item-action>
										<v-btn icon :loading="loadingInvite" @click="cancelPendingInvite(pendingInvite)">
											<v-icon color="grey lighten-1"> mdi-delete </v-icon>
										</v-btn>
									</v-list-item-action>
									<v-list-item-content>
										<v-list-item-title>{{ pendingInvite.email }}</v-list-item-title>
									</v-list-item-content>
								</v-list-item>
							</template>
						</template>
						<template v-else>
							<v-list-item> <v-list-item-subtitle>No pending invites</v-list-item-subtitle> </v-list-item>
						</template>
					</v-list>
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<v-btn color="blue darken-1" :loading="loadingInvite" text @click="pendingInviteDialog = false"> Close </v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<v-snackbar v-model="failedInvite" top>
			Failed to create invite
			<template #action="{ attrs }">
				<v-btn text v-bind="attrs" @click="failedInvite = false"> Close </v-btn>
			</template>
		</v-snackbar>

		<v-snackbar v-model="failedInviteCancel" top>
			Failed to cancel invite
			<template #action="{ attrs }">
				<v-btn text v-bind="attrs" @click="failedInviteCancel = false"> Close </v-btn>
			</template>
		</v-snackbar>
	</div>
</template>

<script>
import { mapActions } from 'vuex';
import UserNavMenu from '@/components/user/UserNavMenu';
import { Invite } from '@/models/user.model';
import { Company } from '@/models/company.model';
import { endpoint, AUTH_BASE_URL, AUTH_API_ENDPOINTS } from '@/utils/api/endpoints';

export default {
	name: 'CompanyUserManagement',

	components: {
		UserNavMenu,
	},

	data() {
		return {
			loadingInvite: false,
			loadingUsers: false,
			newUserDialog: false,
			pendingInviteDialog: false,
			failedInviteCancel: false,
			failedInvite: false,
			pendingInvites: [],
			invite: new Invite(),
			company: new Company(),
			tableHeaders: [
				{ text: 'Actions', value: 'actions', sortable: false },
				{ text: 'Full Name', value: 'fullName' },
				{ text: 'Email', value: 'email' },
				{ text: 'Number', value: 'number' },
			],
			emailRules: [v => !!v || 'E-mail is required', v => /.+@.+\..+/.test(v) || 'E-mail must be valid'],
		};
	},

	computed: {
		filteredUsers() {
			const results = this.normalisedUsers;
			const filteredResults = [];

			if (this.nameSearch) {
				const nameSearch = this.nameSearch.toLowerCase();
				filteredResults.push(...results.filter(x => x.FullName.toString().toLowerCase().includes(nameSearch)));
				return filteredResults;
			}

			return results;
		},

		normalisedUsers() {
			return this.companyManagers.map(x => ({
				id: x.id,
				fullName: `${x.name} ${x.surname}`,
				email: x.email,
				number: x.contact,
				roleNames: x.role,
			}));
		},

		companyManagers() {
			if (!this.company || !this.company.managers) return [];

			return this.company.managers;
		},
	},

	mounted() {
		this.getCompany();
	},

	methods: {
		...mapActions('company', ['InviteCompanyUser', 'GetPendingCompanyUserInvites', 'CancelCompanyUserInvite', 'LoadCompany']),

		editUserRoles() {},

		async sendInvite() {
			if (!this.invite || !this.invite.email) return;
			this.loadingInvite = true;

			const result = await this.InviteCompanyUser(this.invite);

			if (result.success && result.jwtToken) {
				const url = endpoint(AUTH_BASE_URL, AUTH_API_ENDPOINTS.ACCEPT_COMPANY_INVITE, {
					query: {
						token: result.jwtToken,
					},
				});
				this.invite.inviteLink = url;
			}

			this.loadingInvite = false;
		},

		async getPendingInvites() {
			const invites = await this.GetPendingCompanyUserInvites();
			if (!invites || invites.length === 0) {
				this.pendingInvites = [];
				return;
			}

			this.pendingInvites = invites;
		},

		async showPendingInvites() {
			await this.getPendingInvites();
			this.pendingInviteDialog = true;
		},

		closeInviteDialog() {
			this.resetInvite();
			this.newUserDialog = false;
		},

		resetInvite() {
			this.invite = new Invite();
		},

		async cancelPendingInvite(invite) {
			this.loadingInvite = true;

			await this.CancelCompanyUserInvite(invite.id);
			await this.getPendingInvites();

			this.loadingInvite = false;
		},

		async getCompany() {
			const company = await this.LoadCompany();
			this.company = company;
		},
	},
};
</script>

<style></style>
